'use client';
import { Children, cloneElement, isValidElement, ReactNode, useEffect, useRef, useState } from 'react';
import BulletList from '@tiptap/extension-bullet-list'
import Bold from '@tiptap/extension-bold'
import Strike from '@tiptap/extension-strike'
import Italic from '@tiptap/extension-italic'
import Document from '@tiptap/extension-document'
import ListItem from '@tiptap/extension-list-item'
import OrderedList from '@tiptap/extension-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Placeholder from '@tiptap/extension-placeholder';
import Blockquote from '@tiptap/extension-blockquote';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import Underline from '@tiptap/extension-underline';
import Typography from '@tiptap/extension-typography';
import History from '@tiptap/extension-history';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Youtube } from '@tiptap/extension-youtube';
import CustomMention from './CustomMention';
import userSuggestion from './userSuggestion';
import CustomHashtag from './CustomHashtag';
import hashtagSuggestion from './hashtagSuggestion';
import { EditorContent, Editor as TipTapEditor, useEditor } from '@tiptap/react';
import UrlPreviewCard from 'ui/UrlPreviewCard';
import { createRoot, Root } from 'react-dom/client';
import 'tippy.js/dist/tippy.css';
import MenuBar from './MenuBar';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { Type, ImagePlus, ChartBar, ListTodo, ListChecks, ListOrdered, Trash2 } from 'lucide-react';
import { useMediaManager } from 'hooks/useMediaManager';
import MediaManagerCarousel from 'ui/MediaManagerCarousel';
import type { PollDraft } from 'types/poll';
import PollCard from 'components/Comments/PollCard';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'ui/DropdownMenu';
import { resolveMediaUrl } from 'util/mediaUrl';
import { getUploadAccept, validateUploadFile } from 'util/uploadValidation';

interface TextEditorProps {
  value: string;
  onChange: (content: string) => void;
  editorType: 'comment' | 'entity';
  placeholder?: ReactNode | string;
  fieldRequired?: boolean;
  children?: ReactNode;
  action?: 'edit' | 'reply' | 'add' | 'form'
  onCancel?: boolean;
  isEditorExpanded?: boolean;
  showEditorExtensions?: boolean;
  onCommentMediaRequest?: () => void;
  onCommentMediaManagerChange?: (media: import('hooks/useMediaManager').MediaSlot[]) => void;
  initialMedia?: any[]; // For existing media when editing
  commentData?: { id: number }; // For API calls when editing
  pollDraft?: PollDraft | null;
  onPollDraftChange?: (poll: PollDraft | null) => void;
  pollMaxOptions?: number;
}

const TextEditor: React.FC<TextEditorProps> = ({
  value,
  onChange,
  editorType,
  placeholder,
  fieldRequired = false,
  children,
  action = 'form',
  onCancel,
  isEditorExpanded,
  showEditorExtensions = true,
  onCommentMediaRequest,
  onCommentMediaManagerChange,
  initialMedia,
  commentData,
  pollDraft = null,
  onPollDraftChange,
  pollMaxOptions = 6,
}) => {
  const [editorLoaded, setEditorLoaded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [textExists, setTextExists] = useState(false);
  const [isTypeButtonActive, setIsTypeButtonActive] = useState(false);
  const [isExpanded, setIsExpanded] = useState(action === 'edit' || action === 'reply' || false);
  const [showExtensions, setShowExtensions] = useState(showEditorExtensions);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Only allow poll creation for comment create/reply flows
  const canUsePoll = editorType === 'comment' && action !== 'edit' && typeof onPollDraftChange === 'function';
  const resolvedPollMaxOptions = Number.isFinite(pollMaxOptions) && pollMaxOptions > 0 ? pollMaxOptions : 6;
  const resolvedPlaceholder =
    typeof placeholder === "string" && placeholder.trim().length > 0
      ? placeholder.trim()
      : editorType === 'comment' && action === 'add'
        ? 'Comment to start a thread'
        : '';

  // Handle editor expansion state
  useEffect(() => {
    if (action === 'add') {
      setIsExpanded(true);
    } else {
      setIsExpanded(true);
    }
  }, [isFocused, textExists, showExtensions, isExpanded, action, isEditorExpanded]);

  // Create a Link extension that renders UrlPreviewCard inside the editor view
  const PreviewLink = Link.extend({
    renderHTML({ HTMLAttributes }) {
      // Serialize to a normal anchor so saved HTML still contains the link
      // Use 0 to preserve the original text content instead of duplicating the href
      return ['a', HTMLAttributes, 0];
    },
    addNodeView() {
      return ({ node }: { node: any }) => {
        const container = document.createElement('span');
        container.className = 'inline-block w-full';

        let root: Root | null = null;
        const render = (href: string) => {
          if (!root) {
            root = createRoot(container);
          }
          root.render((window as any).React?.createElement
            ? (window as any).React.createElement(UrlPreviewCard, { url: href })
            : (function() { return null as unknown as JSX.Element; })());
        };

        const getHref = (n: any) => n?.attrs?.href || n?.attrs?.src || '';
        render(getHref(node));

        return {
          dom: container,
          update: (updatedNode: any) => {
            render(getHref(updatedNode));
            return true;
          },
          destroy: () => {
            try { root?.unmount(); } catch {}
            root = null;
          },
        } as any;
      };
    },
  }).configure({
    autolink: true,
    linkOnPaste: true,
    openOnClick: true,
    defaultProtocol: 'https',
    HTMLAttributes: {
      class: 'text-editor-link link external-link line-clamp-2',
      rel: 'noopener noreferrer',
    },
  });

  const editor = useEditor({
    extensions: [
      Document,
      Bold,
      Italic,
      Strike,
      ListItem,
      History,
      OrderedList.configure({
        HTMLAttributes: {
          class: 'list-decimal pl-6 my-2',
        },
      }),
      BulletList.configure({
        HTMLAttributes: {
          class: 'list-disc pl-6 my-2',
        },
      }),
      Text,
      Paragraph,
      Blockquote.configure({
        HTMLAttributes: {
          class: 'p-1 bg-eoe-icon dark:bg-eoe-dark-icon border-l-4 border-gray-300 dark:border-eoe-dark-text-heading text-base italic leading-relaxed text-eoe-text-heading dark:text-eoe-dark-text-heading',
          p: 'text-sm italic font-bold leading-relaxed text-eoe-text-heading dark:text-eoe-dark-text-heading',
        },
      }),
      HardBreak,
      Heading,
      Placeholder.configure({
        placeholder: resolvedPlaceholder,
        showOnlyCurrent: true,
        emptyEditorClass: `cursor-text before:text-eoe-text-meta before:dark:text-eoe-dark-text-meta before:absolute before:text-base ${
          isFocused ? 'before:hidden' : 'before:content-[attr(data-placeholder)] after:hidden'
        } ${fieldRequired ? 'after:text-warning-red after:dark:text-dark-warning-red after:ml-[82px] after:absolute after:bottom-[4px]' : ''}`,
      }),
      Underline,
      Typography,
      PreviewLink,
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-[100%]',
        },
        inline: true,
        allowBase64: true
      }),
      Youtube.configure({
        width: 256,
        height: 144,
      }),
      CustomMention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: userSuggestion,
      }),
      CustomHashtag.configure({
        HTMLAttributes: {
          class: 'hashtag',
        },
        suggestion: hashtagSuggestion,
      }),
    ],
    autofocus: (action === 'reply' || action === 'edit'),
    editorProps: {
      attributes: {
        class: `flex flex-col px-3 py-2 text-wrap justify-start items-start w-full gap-x-3 prose-wrapper focus:outline-none`,
      },
      handleDOMEvents: {
        focus: () => {
          setIsFocused(true);
          return false;
        },
        blur: () => {
          if (action === 'edit' || action === 'reply') {
            setIsFocused(false);
            return false;
          }
          setIsFocused(false);
          return true;
        },
      },
    },
    content: value || ``,
    onCreate: () => {
      setEditorLoaded(true);
      if (action === 'edit' || action === 'reply') {
        setTimeout(() => {
          editor?.commands.focus('end');
        }, 0);
      }
    },
    onUpdate({ editor }) {
      const content = editor.getHTML();
      onChange(content);
      setTextExists(editor.getText().length > 0);
    },
  }, [resolvedPlaceholder]);

  const editorRef = useRef<TipTapEditor | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Use media manager and gate its usage by editorType to satisfy hooks rules
  const mediaAll = useMediaManager(20);
  const media = editorType === 'comment' ? mediaAll : null;
  
  // Track deletion state and reordering
  const [deletedMediaKeys, setDeletedMediaKeys] = useState<string[]>([]);
  const [mediaReorderMap, setMediaReorderMap] = useState<Record<string, number>>({});
  const editCarouselApiRef = useRef<any>();

  // Combine existing and new media for unified display
  const [combinedMedia, setCombinedMedia] = useState<import('ui/MediaManagerCarousel').MediaManagerItem[]>([]);

  // Load initial media for editing
  useEffect(() => {
    if (initialMedia && initialMedia.length > 0 && action === 'edit') {
      const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN || '';
      const existingItems: import('ui/MediaManagerCarousel').MediaManagerItem[] = initialMedia.map((m, idx) => ({
        preview: resolveMediaUrl({ key: m.key, url: (m as { url?: string }).url, signed_url: (m as { signed_url?: string }).signed_url }, IMAGE_CDN),
        type: m.type === 'image' ? 'image' : 'video',
        order: idx + 1,
        isExisting: true,
        key: m.key,
        originalOrder: idx + 1  // Track original order
      } as any));
      
      setCombinedMedia(existingItems);
      
      // Initialize the reorder map with original positions
      const initialMap: Record<string, number> = {};
      initialMedia.forEach((m, idx) => {
        initialMap[m.key] = idx + 1;
      });
      setMediaReorderMap(initialMap);
    }
  }, [initialMedia, action]);

  // Update combined media when new files are added
  useEffect(() => {
    if (action === 'edit' && media && media.media.length > 0) {
      setCombinedMedia(prev => {
        // Keep only existing media that hasn't been deleted
        const existingItems = prev.filter((item: any) => item.isExisting && !deletedMediaKeys.includes(item.key));
        // Check if we already have these new items to avoid duplicates
        const existingNewCount = prev.filter((item: any) => !item.isExisting).length;
        if (existingNewCount === media.media.length) {
          return prev; // No change needed, avoid infinite loop
        }
        // Add new media
        const newItems = media.media.map((m, idx) => ({
          ...m,
          order: existingItems.length + idx + 1,
          isExisting: false
        } as any));
        return [...existingItems, ...newItems];
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media?.media.length, action, deletedMediaKeys]); // Use length instead of the whole array

  useEffect(() => {
    if (editor) {
      editorRef.current = editor;
    }
  }, [editor]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
      if (value && value.length > 0) {
        setTextExists(true);
      } else {
        setTextExists(false);
      }
    }
  }, [value, editor]);
  // Handle text content changes
  useEffect(() => {
    if (editor) {
      const content = editor.getText();
      const hasText = content.length > 0;
      setIsExpanded(true);
      setTextExists(hasText);
    }
  }, [editor]);

  // Update text existence when editor content changes
  useEffect(() => {
    const handleUpdate = () => {
      if (editor) {
        const hasText = editor.getText().length > 0;
        setTextExists(hasText);
      }
    };

    if (editor) {
      editor.on('update', handleUpdate);
    }

    return () => {
      if (editor) {
        editor.off('update', handleUpdate);
      }
    };
  }, [editor]);

  // Handle MenuBar visibility
  useEffect(() => {
    setShowExtensions(isTypeButtonActive);
    setIsExpanded(isExpanded);
  }, [isTypeButtonActive, isExpanded]);

  // Notify parent when comment media changes
  useEffect(() => {
    if (editorType === 'comment' && typeof onCommentMediaManagerChange === 'function') {
      if (action === 'edit') {
        // For edit mode, send complete media state including deletions and reordering
        const editMediaState = {
          newMedia: media?.media || [],
          deletedKeys: deletedMediaKeys,
          reorderMap: mediaReorderMap
        } as any;
        onCommentMediaManagerChange(editMediaState);
      } else if (media) {
        // For other modes, just send new media
        onCommentMediaManagerChange(media.media);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorType, media?.media, action, deletedMediaKeys, mediaReorderMap]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;

    const validation = validateUploadFile(file, 'editor-image');
    if (!validation.valid) {
      setUploadError(validation.message);
      return;
    }
    setUploadError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        editor.chain().focus().setImage({ src: result }).run();
      }
    };
    reader.readAsDataURL(file);
    
    // Reset the input value so the same file can be uploaded again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCommentMediaPick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (editorType === 'comment') {
      if (fileInputRef.current) {
        fileInputRef.current.accept = getUploadAccept('media-manager');
        fileInputRef.current.multiple = true;
        fileInputRef.current.onchange = (ev: any) => {
          try {
            const files = (ev.target as HTMLInputElement).files;
            if (files && files.length > 0 && media) {
              media.selectFiles(files);
            }
          } catch {}
        };
        fileInputRef.current.click();
      } else if (typeof onCommentMediaRequest === 'function') {
        onCommentMediaRequest();
      }
      return;
    }
    
    // For entity type, use the original inline image upload behavior
    if (fileInputRef.current) {
      fileInputRef.current.accept = getUploadAccept('editor-image');
      fileInputRef.current.multiple = false;
      fileInputRef.current.onchange = (ev: any) => {
        const syntheticEvent = {
          target: ev.target,
          currentTarget: ev.target
        } as React.ChangeEvent<HTMLInputElement>;
        handleImageUpload(syntheticEvent);
      };
      fileInputRef.current.click();
    }
  };

  /**
   * Remove the current poll draft.
   */
  const handlePollClear = () => {
    onPollDraftChange?.(null);
  };

  const createDefaultPollDraft = (pollType: PollDraft["poll_type"]): PollDraft => ({
    poll_type: pollType,
    statement: "",
    options: [
      { id: `poll-option-${Date.now()}-a`, kind: "text", option_text: "" },
      { id: `poll-option-${Date.now()}-b`, kind: "text", option_text: "" }
    ]
  });

  const handlePollTypeSelect = (pollType: PollDraft["poll_type"]) => {
    if (!onPollDraftChange) return;
    const nextDraft = pollDraft
      ? { ...pollDraft, poll_type: pollType }
      : createDefaultPollDraft(pollType);
    onPollDraftChange(nextDraft);
  };

  return !editorLoaded ? (
    <LoadingSkeleton count={1} height={`${action === 'add' ? 'h-8' : 'h-20'}`} rounded={`${action === 'add' ? 'rounded-full' : 'rounded-2xl'}`} width='w-full' margin='mx-0'/>
  ) : 
  // Add content (ADD, REBUT)
  (action === 'add' ? 
    (
      <div 
        className={`editor-container text-sm 
          border
          text-gray-800 dark:text-eoe-dark-text
          transition-colors ${isExpanded ? 'overflow-y-hidden overflow-x-hidden' : ''}
          ${isExpanded && (isFocused) ? 'rounded-2xl border-[#666666] dark:border-white bg-transparent' : 'bg-gray-200 dark:bg-eoe-dark-input border-eoe-border-main dark:border-eoe-dark-border-main '}
          ${(!isExpanded && action === 'add') ? 'rounded-full max-h-8' : !isEditorExpanded && action === 'add' ? 'rounded-full max-h-8' : 'rounded-2xl'}`}
      >
      {showExtensions && <MenuBar editor={editor} editorType={editorType} />}
      
      {editorType !== 'comment' && (
        <span className={`relative block left-3 text-eoe-text-meta dark:text-eoe-dark-text-meta ${editor?.isFocused ? 'top-2 text-xs' : 'top-3 text-base'} transition-all`}
          onClick={() => {
            setIsFocused(true);
            editor?.commands.focus();
          }}
        >
          {placeholder}
          {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
        </span>
      )}
      
      <EditorContent editor={editor} className="prose-wrapper flex-1 transition-all" />
      {/* Show new media for add action */}
      {editorType === 'comment' && media && media.media.length > 0 && (
        <div className="px-3 pt-2">
          <MediaManagerCarousel
            files={media.media}
            aspectClass={media.aspectClass()}
            onMove={media.move}
            onRemove={media.removeAt}
            setApi={(api) => {
              media.carouselApiRef.current = api as any;
              api?.on('select', () => media.setCurrentIdx(api.selectedScrollSnap()));
            }}
          />
        </div>
      )}

      {canUsePoll && pollDraft && (
        <div className="poll-editor-wrapper mx-3 mt-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-eoe-text-meta dark:text-eoe-dark-text-meta">Poll</span>
            <button
              type="button"
              className="poll-preview-remove inline-flex items-center gap-1 text-xs text-warning-red dark:text-dark-warning-red"
              onClick={handlePollClear}
            >
              <Trash2 className="size-3" />
              Remove
            </button>
          </div>
          <PollCard
            mode="edit"
            draft={pollDraft}
            maxOptions={resolvedPollMaxOptions}
            onDraftChange={(nextDraft) => onPollDraftChange?.(nextDraft)}
          />
        </div>
      )}
      
      <div className={`editor-actions flex-row flex-wrap my-2 gap-2 ${!isExpanded && !isFocused ? 'hidden' : 'flex'}`}>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsFocused(true);
            setIsTypeButtonActive(!isTypeButtonActive);
            editor?.commands.focus();
          }}
          className={`ml-3 w-fit ${isTypeButtonActive ? 'rounded-full bg-eoe-icon-hover dark:bg-eoe-gray p-1' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta px-1'}`}
        >
          <Type className="size-4" />
        </button>
        
        <button
          onClick={handleCommentMediaPick}
          className="w-fit text-eoe-text-meta dark:text-eoe-dark-text-meta px-1"
          title={editorType === 'comment' ? "Upload media" : "Upload image"}
        >
          <ImagePlus className="size-4" />
        </button>

        {canUsePoll && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="poll-editor-button w-fit text-eoe-text-meta dark:text-eoe-dark-text-meta px-1"
                title="Create poll"
              >
                <ChartBar className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="poll-type-menu">
              <DropdownMenuItem onClick={() => handlePollTypeSelect("single_choice")}>
                <ListTodo className="size-4" />
                Single
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handlePollTypeSelect("multi_choice")}>
                <ListChecks className="size-4" />
                Multiple
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handlePollTypeSelect("rank_order")}>
                <ListOrdered className="size-4" />
                Rank
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        
        <input 
          type="file"
          ref={fileInputRef}
          onChange={editorType === 'comment' ? undefined : handleImageUpload}
          accept={editorType === 'comment' ? getUploadAccept('media-manager') : getUploadAccept('editor-image')}
          multiple={editorType === 'comment'}
          className="hidden"
        />
        
        {children && Children.map(children, (child: ReactNode) => {
          if (isValidElement(child)) {
            return cloneElement(child, {
              ...child.props
            });
          }
          return child;
        })}
      </div>
      {uploadError && (
        <p className="mx-3 text-xs text-warning-red dark:text-dark-warning-red">
          {uploadError}
        </p>
      )}
    </div>
  ) : (<div 
    className={`editor-container text-sm 
      border
      text-gray-800 dark:text-eoe-dark-text
      transition-colors ${isExpanded ? 'overflow-y-hidden overflow-x-hidden' : ''}
      ${isExpanded && (isFocused) ? 'rounded-2xl border-[#666666] dark:border-white  bg-transparent' : 'bg-gray-200 dark:bg-eoe-dark-input border-eoe-border-main dark:border-eoe-dark-border-main '}
       rounded-2xl`}
  >
    {showExtensions && <MenuBar editor={editor} editorType={editorType} />}
    
    {editorType !== 'comment' && (
      <span className={`relative block left-3 text-eoe-text-meta dark:text-eoe-dark-text-meta ${isFocused ? 'top-2 text-xs' : 'top-3 text-base'} transition-all`}
        onClick={() => {
          setIsFocused(true);
          editor?.commands.focus();
        }}
      >
        {placeholder}
        {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
      </span>
    )}
    {/* Non-add (EDIT, REPLY) content */}
    <EditorContent editor={editor} className="prose-wrapper flex-1 transition-all" />
    {/* Show unified media carousel for editing (existing + new) */}
    {editorType === 'comment' && action === 'edit' && combinedMedia.length > 0 && (
      <div className="px-3 pt-2">
        <MediaManagerCarousel
          files={combinedMedia}
          aspectClass="aspect-[4/5]"
          setApi={(api) => {
            editCarouselApiRef.current = api;
          }}
          onMove={(idx, dir) => {
            // Calculate new index after move
            let newIndex = idx;
            if (dir === 'left' && idx > 0) {
              newIndex = idx - 1;
            } else if (dir === 'right' && idx < combinedMedia.length - 1) {
              newIndex = idx + 1;
            }
            
            // Reorder combined media locally only
            setCombinedMedia(prev => {
              const arr = [...prev];
              if (dir === 'left' && idx > 0) {
                [arr[idx], arr[idx - 1]] = [arr[idx - 1], arr[idx]];
              } else if (dir === 'right' && idx < arr.length - 1) {
                [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
              }
              const reordered = arr.map((f, i) => ({ ...f, order: i + 1 }));
              
              // Build new reorder map with ALL existing media positions
              const newReorderMap: Record<string, number> = {};
              reordered.forEach((item: any, index) => {
                if (item.isExisting) {
                  newReorderMap[item.key] = index + 1;
                }
              });
              setMediaReorderMap(newReorderMap);
              
              return reordered;
            });
            
            // Scroll carousel to the new position
            setTimeout(() => {
              editCarouselApiRef.current?.scrollTo(newIndex);
            }, 0);
          }}
          onRemove={(idx) => {
            const toRemove = combinedMedia[idx] as any;
            
            if (toRemove.isExisting) {
              // Mark existing media as deleted (to be deleted on save)
              setDeletedMediaKeys(prev => [...prev, toRemove.key]);
              setCombinedMedia(prev => {
                const filtered = prev.filter((_, i) => i !== idx).map((f, i) => ({ ...f, order: i + 1 }));
                
                // Rebuild the entire reorder map for remaining existing media
                const newReorderMap: Record<string, number> = {};
                filtered.forEach((item: any, index) => {
                  if (item.isExisting) {
                    newReorderMap[item.key] = index + 1;
                  }
                });
                setMediaReorderMap(newReorderMap);
                return filtered;
              });
            } else {
              // Remove new media
              if (media) {
                const newMediaIndex = combinedMedia.slice(0, idx).filter((item: any) => !item.isExisting).length;
                media.removeAt(newMediaIndex);
              }
              setCombinedMedia(prev => {
                const filtered = prev.filter((_, i) => i !== idx).map((f, i) => ({ ...f, order: i + 1 }));
                
                // Rebuild reorder map after removing new media
                const newReorderMap: Record<string, number> = {};
                filtered.forEach((item: any, index) => {
                  if (item.isExisting) {
                    newReorderMap[item.key] = index + 1;
                  }
                });
                setMediaReorderMap(newReorderMap);
                return filtered;
              });
            }
          }}
        />
      </div>
    )}
    {/* Show new media for non-edit actions */}
    {editorType === 'comment' && action !== 'edit' && media && media.media.length > 0 && (
      <div className="px-3 pt-2">
        <MediaManagerCarousel
          files={media.media}
          aspectClass={media.aspectClass()}
          onMove={media.move}
          onRemove={media.removeAt}
          setApi={(api) => {
            media.carouselApiRef.current = api as any;
            api?.on('select', () => media.setCurrentIdx(api.selectedScrollSnap()));
          }}
        />
      </div>
    )}

    {canUsePoll && pollDraft && (
      <div className="poll-editor-wrapper mx-3 mt-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-eoe-text-meta dark:text-eoe-dark-text-meta">Poll</span>
          <button
            type="button"
            className="poll-preview-remove inline-flex items-center gap-1 text-xs text-warning-red dark:text-dark-warning-red"
            onClick={handlePollClear}
          >
            <Trash2 className="size-3" />
            Remove
          </button>
        </div>
        <PollCard
          mode="edit"
          draft={pollDraft}
          maxOptions={resolvedPollMaxOptions}
          onDraftChange={(nextDraft) => onPollDraftChange?.(nextDraft)}
        />
      </div>
    )}
    
    <div className={`editor-actions flex-col my-2 justify-start items-start gap-2 ${!isExpanded && !isFocused ? 'hidden' : 'flex'}`}>
      <div className="flex flex-row gap-2 ml-3">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsFocused(true);
            setIsTypeButtonActive(!isTypeButtonActive);
            editor?.commands.focus();
          }}
          className={`${isTypeButtonActive ? 'rounded-full bg-eoe-icon-hover dark:bg-eoe-gray p-1' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta px-1'}`}
        >
          <Type className="size-4" />
        </button>
        
        <button
          onClick={handleCommentMediaPick}
          className="text-eoe-text-meta dark:text-eoe-dark-text-meta px-1"
          title={editorType === 'comment' ? "Upload media" : "Upload image"}
        >
          <ImagePlus className="size-4" />
        </button>

        {canUsePoll && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="poll-editor-button text-eoe-text-meta dark:text-eoe-dark-text-meta px-1"
                title="Create poll"
              >
                <ChartBar className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="poll-type-menu">
              <DropdownMenuItem onClick={() => handlePollTypeSelect("single_choice")}>
                <ListTodo className="size-4" />
                Single
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handlePollTypeSelect("multi_choice")}>
                <ListChecks className="size-4" />
                Multiple
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handlePollTypeSelect("rank_order")}>
                <ListOrdered className="size-4" />
                Rank
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        
        <input 
          type="file"
          ref={fileInputRef}
          onChange={editorType === 'comment' ? undefined : handleImageUpload}
          accept={editorType === 'comment' ? '.jpg,.jpeg,.png,.gif,.webp,.apng,.avif,.svg,.mp4,.mpeg,.mov,.avi,.webm,.ogg,.3gp,.flv' : 'image/*'}
          multiple={editorType === 'comment'}
          className="hidden"
        />
      </div>
      
      {children && Children.map(children, (child: ReactNode) => {
        if (isValidElement(child)) {
          return cloneElement(child, {
            ...child.props
          });
        }
        return child;
      })}
    </div>
  </div>
  ));
};

const styles = `
.ProseMirror {
  width: 100%;
}

.prose-wrapper {
  width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.prose-p {
  width: 100%;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.ProseMirror p {
  width: 100%;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default TextEditor;